/**
 * chatApi — REST helpers for the Team Chat Preact UI.
 *
 * Same-origin `/chat/v2` (session cookie + X-Tenant-Id / X-Person-Id).
 * Wire format matches Api::Chat::V1; mobile JWT stays on api.<domain>/chat/v1.
 *
 * Uses @rails/request.js for CSRF + session cookies. Call configureChatApi
 * once from the Stimulus mount shells before any request.
 */

import { request } from '../../lib/request'
import { REQUEST_TIMEOUT_MS, ApiError } from '@tshmanager/chat-shared'
import {
  chat_v2_rooms_path,
  chat_v2_room_path,
  involvement_chat_v2_room_path,
  purge_chat_v2_room_path,
  direct_chat_v2_rooms_path,
  chat_v2_room_messages_path,
  chat_v2_room_message_path,
  chat_v2_room_message_show_path,
  read_chat_v2_room_messages_path,
  delivered_chat_v2_room_message_path,
  chat_v2_room_message_reactions_path,
  chat_v2_room_typing_index_path,
  chat_v2_search_path,
  chat_v2_persons_path,
  chat_v2_person_path,
  chat_v2_task_path,
  presign_chat_v2_room_attachments_path,
  finalize_chat_v2_room_message_attachments_path,
  chat_v2_attachment_download_path,
} from '../../routes'

let _tenantId = null
let _personId = null

/** Call from Stimulus mount shells before chatApi is used. */
export function configureChatApi({ tenantId, personId } = {}) {
  if (tenantId != null && tenantId !== '') _tenantId = String(tenantId)
  if (personId != null && personId !== '') _personId = String(personId)
}

export function chatApiHeaders(extra = {}) {
  const headers = { ...extra }
  if (_tenantId) headers['X-Tenant-Id'] = _tenantId
  if (_personId) headers['X-Person-Id'] = _personId
  return headers
}

function opts(extra = {}) {
  return {
    responseKind: 'json',
    ...extra,
    headers: chatApiHeaders(extra.headers || {}),
  }
}

function jsonBody(body) {
  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  }
}

export const chatApi = {
  // ── Rooms ──────────────────────────────────────────────────────────────
  rooms: {
    /** GET /chat/v2/rooms → Room[] */
    list: () => request.get(chat_v2_rooms_path(), opts()),

    /** GET /chat/v2/rooms/:id → Room */
    show: (id) => request.get(chat_v2_room_path(id), opts()),

    /**
     * POST /chat/v2/rooms
     * Body: { name, member_person_ids }
     */
    create: (params) =>
      request.post(chat_v2_rooms_path(), opts(jsonBody(params))),

    /**
     * PATCH /chat/v2/rooms/:id
     * Body: { name?, add_person_ids?, remove_person_ids?, avatar_signed_id? }
     */
    update: (id, params) =>
      request.patch(chat_v2_room_path(id), opts(jsonBody(params))),

    /**
     * DELETE /chat/v2/rooms/:id — leave room (destroy membership).
     */
    destroy: (id) => request.delete(chat_v2_room_path(id), opts()),

    /**
     * DELETE /chat/v2/rooms/:id/purge — permanently destroy for all members.
     */
    purge: (id) => request.delete(purge_chat_v2_room_path(id), opts()),

    /**
     * POST /chat/v2/rooms/direct
     * Body: { person_id }
     */
    direct: (personId) =>
      request.post(direct_chat_v2_rooms_path(), opts(jsonBody({ person_id: personId }))),

    /** PATCH /chat/v2/rooms/:id/involvement — { involvement: everything|mentions|nothing } */
    updateInvolvement: (id, involvement) =>
      request.patch(
        involvement_chat_v2_room_path(id),
        opts(jsonBody({ involvement }))
      ),
  },

  // ── Messages ───────────────────────────────────────────────────────────
  messages: {
    /**
     * GET /chat/v2/rooms/:roomId/messages?before=:before
     */
    list: (roomId, { before } = {}) => {
      const query = before ? { before } : undefined
      return request.get(chat_v2_room_messages_path(roomId), opts({ query }))
    },

    /**
     * GET /chat/v2/rooms/:roomId/messages/:id
     */
    show: (roomId, id) =>
      request.get(chat_v2_room_message_show_path(roomId, id), opts()),

    /**
     * POST /chat/v2/rooms/:roomId/messages
     * Body: { body, signed_ids: [], reply_to_id, client_message_id }
     * (same wire as mobile chat / Api::Chat::V1)
     */
    create: (roomId, body) =>
      request.post(chat_v2_room_messages_path(roomId), opts(jsonBody(body))),

    /**
     * PATCH /chat/v2/rooms/:roomId/messages/:id
     * Body: { body }
     */
    update: (roomId, id, body) =>
      request.patch(chat_v2_room_message_path(roomId, id), opts(jsonBody(body))),

    /**
     * DELETE /chat/v2/rooms/:roomId/messages/:id
     */
    destroy: (roomId, id) =>
      request.delete(chat_v2_room_message_path(roomId, id), opts()),

    /**
     * POST /chat/v2/rooms/:roomId/messages/read
     */
    read: (roomId) =>
      request.post(read_chat_v2_room_messages_path(roomId), opts()),

    /**
     * POST /chat/v2/rooms/:roomId/messages/:msgId/delivered
     */
    delivered: (roomId, msgId) =>
      request.post(delivered_chat_v2_room_message_path(roomId, msgId), opts()),
  },

  // ── Typing ─────────────────────────────────────────────────────────────
  typing: {
    /**
     * POST /chat/v2/rooms/:roomId/typing
     * Body: { typing: Boolean }
     */
    send: (roomId, typing) =>
      request.post(chat_v2_room_typing_index_path(roomId), opts(jsonBody({ typing }))),
  },

  // ── Reactions ──────────────────────────────────────────────────────────
  reactions: {
    /**
     * POST /chat/v2/rooms/:roomId/messages/:msgId/reactions
     * Body: { emoji }
     */
    toggle: (roomId, msgId, emoji) =>
      request.post(chat_v2_room_message_reactions_path(roomId, msgId), opts(jsonBody({ emoji }))),
  },

  // ── Attachments ────────────────────────────────────────────────────────
  attachments: {
    /**
     * POST /chat/v2/rooms/:roomId/attachments/presign
     */
    presign: (roomId, params) =>
      request.post(presign_chat_v2_room_attachments_path(roomId), opts(jsonBody(params))),

    /**
     * POST /chat/v2/rooms/:roomId/messages/:msgId/attachments/finalize
     */
    finalize: (roomId, msgId, params) =>
      request.post(
        finalize_chat_v2_room_message_attachments_path(roomId, msgId),
        opts(jsonBody(params))
      ),

    /** Download URL string (not a fetch). */
    download: (signedId) => chat_v2_attachment_download_path(signedId),
  },

  // ── Search ─────────────────────────────────────────────────────────────
  search: {
    /** GET /chat/v2/search?q=:q → Room[] */
    rooms: (q) =>
      request.get(chat_v2_search_path(), opts({ query: { q } })),
  },

  // ── Persons ────────────────────────────────────────────────────────────
  persons: {
    /**
     * GET /chat/v2/persons?q=:q → Person[]
     * Tenant-scoped; used for @mentions and modal member pickers.
     */
    search: (_roomId, q) =>
      request.get(chat_v2_persons_path(), opts({ query: q ? { q } : undefined })),

    /**
     * GET /chat/v2/persons/:id → PersonProfile
     */
    profile: (_roomId, personId) =>
      request.get(chat_v2_person_path(personId), opts()),

    /** Alias without unused roomId (modals / DM picker). */
    list: (q) =>
      request.get(chat_v2_persons_path(), opts({ query: q ? { q } : undefined })),

    show: (personId) =>
      request.get(chat_v2_person_path(personId), opts()),
  },

  // ── Direct rooms (compat alias) ────────────────────────────────────────
  directs: {
    /** POST /chat/v2/rooms/direct with person_id → Room */
    open: (personId) => chatApi.rooms.direct(personId),
  },

  // ── Tasks ──────────────────────────────────────────────────────────────
  tasks: {
    /** GET /chat/v2/tasks/:id */
    show: (id) => request.get(chat_v2_task_path(id), opts()),
  },
}

export { ApiError, REQUEST_TIMEOUT_MS }
